// Pure logic behind the create / edit tournament modal (js/ui/tournament-form.js).
//
// That file is DOM wiring end to end: reading inputs, filling inputs, showing and
// hiding panels, building <option> markup. Everything in between — the default a
// blank field falls back to, the normalisation a value goes through on its way into
// a tournament, the mapping between the form's values and the stored tournament /
// group / template shapes, the validation the submit handler runs, and the small
// text builders — lives here instead, so it can be exercised without a browser
// (tests/tournament-form-logic.test.js).
//
// The rule for what belongs here: a function takes plain values and returns plain
// values. Nothing in this module touches `document`, and nothing reads or writes
// `state` — the UI passes in what a helper needs (the tournament, its groups, the
// organization record, the player list) and applies whatever it hands back.
//
// It does import four NORMALISERS from js/state.js — defaultSeedColor,
// normalizeMaxParticipants, teamSettingsFrom and konradSettingsFrom. Those are pure
// functions and they are the single definition of what a blank seed colour, a blank
// board count or a blank participant ceiling means; restating them here is exactly
// the drift their own comments warn about. They are the only reason this module is
// not import-free, and the reason the tests install tests/helpers/browser-shims.js
// before importing it (js/state.js reaches for localStorage as it loads).
//
// User-facing strings go through the global tr() (js/i18n.js), same as the UI file.

import {
    defaultSeedColor,
    normalizeMaxParticipants,
    teamSettingsFrom,
    konradSettingsFrom,
    accelerationSettingsFrom
} from '../state.js';
import { tournamentOrganizerFields } from './organizer-logic.js';
import { osloDate } from './oslo-date.js';

// The tie-breaks a tournament starts with when nothing else has chosen: Buchholz,
// Buchholz cut-1, direct encounter.
export const DEFAULT_TIEBREAKS = Object.freeze(['bh', 'bh1', 'de']);

// One <select> binds a player database and one of its rating lists, so its option
// values carry "dbId|listId" (see the long note in js/ui/tournament-form.js).
export const RATING_LIST_SEPARATOR = '|';

// ── Small shared predicates ────────────────────────────────────────────────

// A draft tournament is the half-built one the groups editor hangs its groups off
// while the create modal is open; it is recognised by its id, and never saved.
export function isDraftId(id) {
    return String(id ?? '').startsWith('draft_');
}

// A tournament's groups, always an array.
export function groupsOf(tournament) {
    return (tournament && Array.isArray(tournament.groups)) ? tournament.groups : [];
}

// Groups are matched by string id throughout: ids arrive as numbers from older
// tournaments and as strings from dataset attributes.
export function findGroupById(groups, id) {
    return (Array.isArray(groups) ? groups : []).find(g => String(g.id) === String(id)) || null;
}

// Two or more groups: the precondition for "apply to all" and for merging.
export function hasMultipleGroups(groups) {
    return (Array.isArray(groups) ? groups : []).length >= 2;
}

// ISO 'YYYY-MM-DD' for a timestamp — what <input type="date"> expects, and the
// default start date for a tournament created without one. Norway's date, not
// UTC's: a tournament created just after midnight is for today, not yesterday.
export function todayIso(now = Date.now()) {
    return osloDate(now);
}

// A staged tie-break list (from a template) if there is one, else the defaults.
// Always a fresh array, so the tournament can never share it with the draft.
export function tiebreaksOrDefault(draftTiebreaks) {
    return (Array.isArray(draftTiebreaks) && draftTiebreaks.length)
        ? [...draftTiebreaks]
        : [...DEFAULT_TIEBREAKS];
}

// Whether a registration toggle is off because the first round was paired, rather
// than because anyone switched it off (state.js closeRegistrationOnFirstPairing
// sets the flag). Drives the explanatory note under the toggle.
export function isRegistrationClosedByPairing(src) {
    return !!(src && src.registrationClosedByPairing && !src.registrationOpen);
}

// ── The groups tab bar inside the modal ────────────────────────────────────

// Whether the modal shows its per-group tabs at all. While EDITING, any tournament
// with groups shows them. While CREATING, only a draft does — the real active
// tournament's groups have nothing to do with the one being created.
export function modalGroupsVisible(tournament, isEdit) {
    if (!tournament || !tournament.groups || tournament.groups.length === 0) return false;
    return isEdit ? true : isDraftId(tournament.id);
}

// The NSF series a group (or a tournament without groups) belongs to: 'ngp', 'bgp', or ''
// for any other tournament. With "NSF Membership Required" on, it decides who may enter
// (js/utils/nsf-membership.js nsfGroupRules); with it off, it is kept and means nothing.
export function nsfSeriesValue(v) {
    const s = String(v || '').toLowerCase();
    return s === 'ngp' || s === 'bgp' ? s : '';
}

// The per-group settings inputs, keyed by their DOM id. The UI wires one listener
// per entry; the table is data, so it is pinned by a test rather than by reading
// the wiring code.
//
// EVERY ENTRY IS ONE INPUT TO ONE FLAT PROPERTY. Two of the group's settings are not
// that shape and are wired by hand in js/ui/tournament-form.js instead: acceleration is
// a nested object whose two boxes must be read together so the normaliser writes a whole
// one, and the prize categories are a list behind a button. Adding either here would
// write a string where an object or an array belongs.
export const GROUP_INPUT_FIELDS = Object.freeze({
    'group-name-input': 'name',
    'group-format-input': 'format',
    'group-type-input': 'type',
    'group-rounds-input': 'roundsCount',
    'group-seed-color-input': 'seedColor',
    'group-points-input': 'pointSystem',
    'group-use-dates-input': 'useGroupDates',
    'group-start-date-input': 'startDate',
    'group-start-time-input': 'startTime',
    'group-reg-open-input': 'registrationOpen',
    'group-reg-deadline-input': 'registrationDeadline',
    'group-reg-deadline-time-input': 'registrationDeadlineTime',
    'group-max-participants-input': 'maxParticipants',
    'group-waiting-list-input': 'waitingList',
    'group-require-phone-input': 'requirePhone',
    'group-require-nsf-input': 'requireNsfMembership',
    'group-nsf-series-input': 'nsfSeries',
    'group-reg-approval-input': 'registrationApproval',
    'group-keep-reg-open-input': 'keepRegistrationOpen'
});

// A stored group as the per-group settings inputs want to show it. 0 / absent for
// the participant maximum is "no limit" and shows as EMPTY rather than as a literal
// 0 — the placeholder already says "No limit", and a 0 in a "maximum participants"
// box reads like a tournament nobody may enter.
export function groupFormValues(group) {
    const g = group || {};
    return {
        name: g.name || '',
        format: g.format || 'swiss',
        type: g.type || 'standard',
        roundsCount: g.roundsCount || 7,
        seedColor: g.seedColor || defaultSeedColor(g.format),
        pointSystem: g.pointSystem || '1-0.5-0',
        // Always fully shaped, so the panel that fills the boxes never has to guard each
        // field. Not in GROUP_INPUT_FIELDS — see the note on that table.
        acceleration: accelerationSettingsFrom(g.acceleration),
        useGroupDates: !!g.useGroupDates,
        startDate: g.startDate || '',
        startTime: g.startTime || '09:00',
        registrationOpen: !!g.registrationOpen,
        registrationDeadline: g.registrationDeadline || '',
        registrationDeadlineTime: g.registrationDeadlineTime || '23:59',
        maxParticipants: normalizeMaxParticipants(g.maxParticipants) || '',
        waitingList: g.waitingList === true,
        requirePhone: !!g.requirePhone,
        requireNsfMembership: g.requireNsfMembership === true,

        nsfSeries: nsfSeriesValue(g.nsfSeries),
        registrationApproval: g.registrationApproval || 'review',
        keepRegistrationOpen: !!g.keepRegistrationOpen
    };
}

// What an edited per-group input writes back onto the group. The round count and
// the participant maximum are stored as NUMBERS, never as the string the box holds:
// the maximum is compared against a player count on the server, and '70' > 7 is not
// the comparison anyone wants.
export function coerceGroupInputValue(prop, raw, isCheckbox = false) {
    if (isCheckbox) return !!raw;
    if (prop === 'roundsCount') return parseInt(raw, 10) || 7;
    if (prop === 'maxParticipants') return normalizeMaxParticipants(raw);
    return raw;
}

// ── Folders (the modal's Folder <select>) ──────────────────────────────────

// Folders in the order the picker lists them: by name, case- and accent-insensitive.
export function sortFoldersByName(folders) {
    return ((Array.isArray(folders) ? folders : []).slice())
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
}

// The folder currently assigned to a tournament id (or '' for none).
export function folderIdFor(org, tournamentId) {
    const rec = org && org.items && org.items[tournamentId];
    return rec && rec.folderId ? String(rec.folderId) : '';
}

// The organization record a tournament should get after the modal's folder choice,
// or null when the choice changes nothing (so the caller skips the cloud write).
export function folderAssignmentChange(org, tournamentId, folderId) {
    if (!tournamentId) return null;
    const items = (org && org.items) || {};
    const existing = items[tournamentId] || { folderId: null, pinned: false, order: null };
    const next = folderId || null;
    if (String(existing.folderId || '') === String(next || '')) return null;
    return { ...existing, folderId: next };
}

// A brand new folder, created inline from the modal.
export function makeFolderRecord(name, { now = Date.now(), suffix } = {}) {
    const tail = suffix === undefined ? Math.random().toString(36).slice(2, 7) : suffix;
    return {
        id: 'folder_' + now + '_' + tail,
        name,
        createdAt: now,
        pinned: false
    };
}

// ── Filling the form when editing ──────────────────────────────────────────

// A stored tournament (plus its active settings, which for a multi-group event come
// from the active group) as the General tab's inputs want to show it.
export function editFormValues(tournament, settings) {
    const t = tournament || {};
    const s = settings || {};
    return {
        // Which club runs it (js/ui/organizers-page.js). The name is the copy the
        // tournament carries, so a club the editing arbiter is not in still shows.
        organizerId: t.organizerId || '',
        organizerName: t.organizerName || '',
        name: t.name || '',
        startDate: t.startDate || '',
        startTime: t.startTime || '09:00',
        endDate: t.endDate || '',
        endTime: t.endTime || '18:00',
        format: s.format,
        type: s.type,
        rounds: s.roundsCount,
        seedColor: s.seedColor,
        pointSystem: s.pointSystem,
        location: t.location || '',
        timeControl: t.timeControl || '',
        arbiterName: t.arbiterName || '',
        arbiterEmail: t.arbiterEmail || '',
        arbiterPhone: t.arbiterPhone || '',
        isPublic: t.isPublic || false,
        // '' = ask each time Print Pairings is used (js/ui/print.js)
        pairingPrintStyle: t.pairingPrintStyle || '',
        // 'auto' = a round is visible to spectators the moment it is paired,
        // which is what every tournament did before this setting (js/ui/pairings.js)
        pairingPublish: t.pairingPublish === 'manual' ? 'manual' : 'auto',
        welcomeMessage: t.welcomeMessage || '',
        registrationOpen: t.registrationOpen || false,
        registrationDeadline: t.registrationDeadline || '',
        registrationDeadlineTime: t.registrationDeadlineTime || '23:59',
        maxParticipants: normalizeMaxParticipants(t.maxParticipants) || '',
        waitingList: t.waitingList === true,
        requirePhone: t.requirePhone || false,
        requireNsfMembership: t.requireNsfMembership === true,

        nsfSeries: nsfSeriesValue(t.nsfSeries),
        registrationApproval: t.registrationApproval || 'review',
        keepRegistrationOpen: t.keepRegistrationOpen || false
    };
}

// ── The draft tournament the groups editor hangs groups off ────────────────

// Turn the modal's General tab into the draft tournament that "Add Group" attaches
// its first group to.
//
// EVERY General field is carried across, which is the whole point. This used to
// invent a 7-round Swiss with standard ratings and no registration, so an organizer
// who configured the tournament and then pressed "Add Group" watched every one of
// those choices evaporate — and since each further group inherits from the previous
// one (addGroup -> inheritedGroupSettings in state.js), the wrong settings then
// propagated through the whole event.
//
//   values  — the raw General-tab values, keyed as below (missing = '').
//   extras  — { teamSettings, konradSettings, ratingListSelection, draftTiebreaks, now }.
export function buildDraftTournament(values = {}, extras = {}) {
    const v = (k) => {
        const raw = values[k];
        return raw === undefined || raw === null ? '' : String(raw);
    };
    const now = extras.now === undefined ? Date.now() : extras.now;
    const rounds = parseInt(v('rounds'), 10) || 7;

    return {
        id: 'draft_' + now,
        ...tournamentOrganizerFields(values.organizerId, values.organizerName),
        name: v('name') || tr('New Tournament'),
        startDate: v('startDate') || todayIso(now),
        startTime: v('startTime') || '09:00',
        endDate: v('endDate'),
        endTime: v('endTime') || '18:00',
        format: v('format') || 'swiss',
        type: v('type') || 'standard',
        rounds,
        roundsCount: rounds,
        seedColor: v('seedColor') || 'white',
        pointSystem: v('pointSystem') || '1-0.5-0',
        teamSettings: extras.teamSettings,
        konradSettings: extras.konradSettings,
        acceleration: extras.acceleration,
        // Tie-breaks have no input on the General tab; a template may have staged them.
        tiebreaks: tiebreaksOrDefault(extras.draftTiebreaks),
        ...(extras.ratingListSelection || {}),
        location: v('location'),
        timeControl: v('timeControl').trim(),
        arbiterName: v('arbiterName').trim(),
        arbiterEmail: v('arbiterEmail').trim(),
        arbiterPhone: v('arbiterPhone').trim(),
        isPublic: !!values.isPublic,
        welcomeMessage: v('welcomeMessage'),
        registrationOpen: !!values.registrationOpen,
        registrationDeadline: v('registrationDeadline'),
        registrationDeadlineTime: v('registrationDeadlineTime') || '23:59',
        requirePhone: !!values.requirePhone,
        requireNsfMembership: !!values.requireNsfMembership,

        nsfSeries: nsfSeriesValue(values.nsfSeries),
        registrationApproval: v('registrationApproval') || 'auto',
        keepRegistrationOpen: !!values.keepRegistrationOpen,
        maxParticipants: normalizeMaxParticipants(v('maxParticipants')),
        waitingList: !!values.waitingList
    };
}

// ── Templates ──────────────────────────────────────────────────────────────

// The pairing system's name as a template row shows it.
function formatLabel(format) {
    return tr(({
        'swiss': 'Swiss', 'monrad': 'Monrad', 'konrad': 'Konrad', 'arena': 'Arena',
        'round-robin': 'Round Robin', 'double-round-robin': 'Double RR'
    }[format] || 'Swiss'));
}

// A one-line summary of a template's settings for the template list rows.
export function formatTemplateSummary(tmpl) {
    const s = (tmpl && tmpl.settings) || {};

    if (Array.isArray(s.groups) && s.groups.length > 0) {
        return `${tr(s.groups.length === 1 ? '{n} group' : '{n} groups', { n: s.groups.length })} • ${s.groups.map(g => formatLabel(g.format)).join(', ')}`;
    }
    const parts = [formatLabel(s.format)];
    if (s.type) parts.push(tr(s.type.charAt(0).toUpperCase() + s.type.slice(1)));
    if (s.rounds) parts.push(tr('{n} rounds', { n: s.rounds }));
    return parts.join(' • ');
}

// The line under "From a template" in the new-tournament choice modal.
export function templateCountHint(count) {
    const n = Number(count) || 0;
    return n > 0
        ? tr(n === 1 ? '{n} saved template' : '{n} saved templates', { n })
        : tr('No templates saved yet.');
}

// One group as a template stores it: settings only, no name-independent data
// (players, results, dates) and no ids.
export function templateGroupSettings(group) {
    const g = group || {};
    return {
        name: g.name,
        format: g.format || 'swiss',
        type: g.type || 'standard',
        roundsCount: g.roundsCount || 7,
        pointSystem: g.pointSystem || '1-0.5-0',
        seedColor: g.seedColor || defaultSeedColor(g.format),
        tiebreaks: g.tiebreaks || [...DEFAULT_TIEBREAKS],
        teamSettings: teamSettingsFrom(g.teamSettings),
        konradSettings: konradSettingsFrom(g.konradSettings),
        acceleration: accelerationSettingsFrom(g.acceleration),
        ratingDbId: g.ratingDbId || '',
        ratingListId: g.ratingListId || '',
        ratingListName: g.ratingListName || '',
        useGroupDates: !!g.useGroupDates,
        startTime: g.startTime || '09:00',
        registrationOpen: !!g.registrationOpen,
        registrationDeadlineTime: g.registrationDeadlineTime || '23:59',
        requirePhone: !!g.requirePhone,
        requireNsfMembership: g.requireNsfMembership === true,

        nsfSeries: nsfSeriesValue(g.nsfSeries),
        registrationApproval: g.registrationApproval || 'review',
        keepRegistrationOpen: !!g.keepRegistrationOpen,
        maxParticipants: normalizeMaxParticipants(g.maxParticipants),
        waitingList: g.waitingList === true
    };
}

// The `settings` object a template is saved with. Mirrors what the create/submit
// flow reads; players, dates, name and location are deliberately excluded.
//
//   values  — the raw General-tab values (see buildDraftTournament).
//   opts    — { groups, teamSettings, konradSettings, ratingListSelection, tiebreaks }.
//             `groups` present and non-empty → a multi-group template, and the
//             single-group half is left out entirely.
export function buildTemplateSettings(values = {}, opts = {}) {
    const v = (k) => {
        const raw = values[k];
        return raw === undefined || raw === null ? '' : String(raw);
    };

    const settings = {
        timeControl: v('timeControl').trim(),
        // Contact details travel with the template: the same arbiter runs the
        // club's events, and retyping their address for each one is the kind of
        // chore a template exists to remove.
        arbiterName: v('arbiterName').trim(),
        arbiterEmail: v('arbiterEmail').trim(),
        arbiterPhone: v('arbiterPhone').trim(),
        isPublic: !!values.isPublic,
        welcomeMessage: v('welcomeMessage'),
        registrationOpen: !!values.registrationOpen,
        registrationDeadlineTime: v('registrationDeadlineTime'),
        requirePhone: !!values.requirePhone,
        requireNsfMembership: !!values.requireNsfMembership,

        nsfSeries: nsfSeriesValue(values.nsfSeries),
        registrationApproval: v('registrationApproval'),
        keepRegistrationOpen: !!values.keepRegistrationOpen,
        // A club that runs the same event in the same hall every month has the
        // same number of boards every month, so the ceiling travels with the
        // template like every other registration setting.
        maxParticipants: normalizeMaxParticipants(v('maxParticipants')),
        waitingList: !!values.waitingList
    };

    const groups = Array.isArray(opts.groups) ? opts.groups : null;
    if (groups && groups.length > 0) {
        settings.groups = groups.map(templateGroupSettings);
    } else {
        settings.format = v('format');
        settings.type = v('type');
        settings.rounds = parseInt(v('rounds'), 10) || 7;
        settings.seedColor = v('seedColor');
        settings.pointSystem = v('pointSystem');
        settings.teamSettings = opts.teamSettings;
        settings.konradSettings = opts.konradSettings;
        settings.acceleration = opts.acceleration;
        Object.assign(settings, opts.ratingListSelection || {});
        settings.tiebreaks = opts.tiebreaks ? opts.tiebreaks : [...DEFAULT_TIEBREAKS];
    }
    return settings;
}

// Does this template describe a multi-group event?
export function templateHasGroups(settings) {
    const s = settings || {};
    return Array.isArray(s.groups) && s.groups.length > 0;
}

// The General-tab values a template pre-fills (every template carries these,
// whether or not it has groups).
export function templateFormValues(settings) {
    const s = settings || {};
    return {
        timeControl: s.timeControl || '',
        arbiterName: s.arbiterName || '',
        arbiterEmail: s.arbiterEmail || '',
        arbiterPhone: s.arbiterPhone || '',
        isPublic: !!s.isPublic,
        welcomeMessage: s.welcomeMessage || '',
        registrationOpen: !!s.registrationOpen,
        registrationDeadlineTime: s.registrationDeadlineTime || '23:59',
        maxParticipants: normalizeMaxParticipants(s.maxParticipants) || '',
        waitingList: s.waitingList === true,
        requirePhone: !!s.requirePhone,
        requireNsfMembership: s.requireNsfMembership === true,

        nsfSeries: nsfSeriesValue(s.nsfSeries),
        registrationApproval: s.registrationApproval || 'review',
        keepRegistrationOpen: !!s.keepRegistrationOpen
    };
}

// The single-group half of a template, for the format/rounds/colour inputs.
// `tiebreaks` is null when the template has none — the submit handler stages it
// because there is no tie-break input on this form.
export function templateSingleGroupValues(settings) {
    const s = settings || {};
    return {
        format: s.format || 'swiss',
        type: s.type || 'standard',
        rounds: s.rounds || 7,
        seedColor: s.seedColor || defaultSeedColor(s.format),
        pointSystem: s.pointSystem || '1-0.5-0',
        teamSettings: s.teamSettings,
        konradSettings: s.konradSettings,
        acceleration: s.acceleration,
        tiebreaks: Array.isArray(s.tiebreaks) ? s.tiebreaks : null
    };
}

// A draft multi-group tournament built from a template, so the create/submit flow
// treats it as multi-group. Dates, name and location come from the form (`general`),
// everything else from the template.
//
//   opts — { now, randomSuffix } (randomSuffix is injectable so group ids are
//          reproducible in tests).
export function buildTemplateDraftTournament(settings, general = {}, opts = {}) {
    const s = settings || {};
    const base = opts.now === undefined ? Date.now() : opts.now;
    const randomSuffix = opts.randomSuffix || (() => Math.random().toString(36).substring(2, 7));
    const g = (k) => general[k] || '';

    return {
        id: 'draft_' + base,
        name: g('name') || tr('New Tournament'),
        startDate: g('startDate') || todayIso(base),
        startTime: g('startTime') || '09:00',
        endDate: g('endDate') || '',
        endTime: g('endTime') || '18:00',
        location: g('location') || '',
        timeControl: s.timeControl || '',
        arbiterName: s.arbiterName || '',
        arbiterEmail: s.arbiterEmail || '',
        arbiterPhone: s.arbiterPhone || '',
        isPublic: !!s.isPublic,
        welcomeMessage: s.welcomeMessage || '',
        groups: (s.groups || []).map((grp, i) => ({
            id: 'group_' + base + '_' + i + '_' + randomSuffix(i),
            name: grp.name || tr('Group {letter}', { letter: String.fromCharCode(65 + i) }),
            format: grp.format || 'swiss',
            type: grp.type || 'standard',
            roundsCount: grp.roundsCount || 7,
            pointSystem: grp.pointSystem || '1-0.5-0',
            seedColor: grp.seedColor || defaultSeedColor(grp.format),
            tiebreaks: grp.tiebreaks || [...DEFAULT_TIEBREAKS],
            konradSettings: konradSettingsFrom(grp.konradSettings),
            acceleration: accelerationSettingsFrom(grp.acceleration),
            ratingDbId: grp.ratingDbId || '',
            ratingListId: grp.ratingListId || '',
            ratingListName: grp.ratingListName || '',
            useGroupDates: !!grp.useGroupDates,
            // Dates are the one thing a template never carries: they belong to the
            // event being created, not to the recipe it was created from.
            startDate: '',
            startTime: grp.startTime || '09:00',
            registrationOpen: !!grp.registrationOpen,
            registrationDeadline: '',
            registrationDeadlineTime: grp.registrationDeadlineTime || '23:59',
            requirePhone: !!grp.requirePhone,
            requireNsfMembership: grp.requireNsfMembership === true,

            nsfSeries: nsfSeriesValue(grp.nsfSeries),
            registrationApproval: grp.registrationApproval || 'review',
            keepRegistrationOpen: !!grp.keepRegistrationOpen,
            maxParticipants: normalizeMaxParticipants(grp.maxParticipants),
            waitingList: grp.waitingList === true,
            players: [],
            rounds: []
        }))
    };
}

// ── Format-dependent panels and the round-count suggestion ─────────────────

// Which of the format-specific setting panels belong on screen.
//
// Acceleration is offered for the Swiss system alone. It is carried to the engine as XXA
// records in the pairing file (js/services/pairing-trf.js), and the other formats either
// pair in-house or follow a fixed schedule, so there is nowhere for the setting to go.
export function formatPanelVisibility(format) {
    return {
        team: format === 'team',
        konrad: format === 'konrad',
        arena: format === 'arena',
        acceleration: format === 'swiss'
    };
}

// The round count a round robin implies, or null when the format does not imply
// one (so the caller leaves the box alone).
//
// A TEAM round robin is counted in TEAMS, not players — four teams of four play
// three rounds, not fifteen — so the field size comes from a different place.
// A round robin needs n-1 rounds (n the field rounded up to even), capped at 50.
export function suggestedRoundCount({ format, teamPairingSystem, playerCount = 0, teamCount = 0 } = {}) {
    const isTeamRR = format === 'team' && teamPairingSystem === 'round-robin';
    if (format !== 'round-robin' && !isTeamRR) return null;

    const field = isTeamRR ? teamCount : playerCount;
    if (field < 2) return null;

    const n = field % 2 === 0 ? field : field + 1;
    return Math.min(n - 1, 50);
}

// ── Database and rating list binding ───────────────────────────────────────

// The <select> value that stands for a stored binding. A database bound with no
// list still round-trips, as "dbId|".
export function ratingListValueFor(selected) {
    return selected && selected.ratingDbId
        ? selected.ratingDbId + RATING_LIST_SEPARATOR + (selected.ratingListId || '')
        : '';
}

// The option model for the rating-list <select>.
//
// A database is offered TWICE OVER: once on its own ("dbId|", no list) and once per
// rating list it has. Binding the database alone is a complete way to use it — the
// roster searches it and players can be added back to it — and nothing is rated,
// because every rating path is gated on a list id. It is also the ONLY way to
// attach a database that has no rating lists.
//
// `unavailable` is a trailing option for a database or list the arbiter has since
// lost access to (or that was deleted); without it the binding would silently reset
// to "none" on the next save of a tournament whose edit never touched ratings.
export function buildRatingListOptions(databases, selected) {
    const dbs = Array.isArray(databases) ? databases : [];
    const chosen = ratingListValueFor(selected);

    const none = { value: '', label: tr('No custom database'), listName: '' };
    const groups = dbs.map(d => ({
        label: d.dbName,
        options: [{ value: d.dbId + RATING_LIST_SEPARATOR, label: tr('Players only — no ratings'), listName: '' }]
            .concat((d.lists || []).map(l => ({
                value: d.dbId + RATING_LIST_SEPARATOR + l.listId,
                label: l.listName,
                listName: l.listName
            })))
    }));

    let unavailable = null;
    const known = [none].concat(...groups.map(g => g.options)).some(o => o.value === chosen);
    if (chosen && !known) {
        unavailable = {
            value: chosen,
            label: (selected.ratingListName || tr('Database')) + ' (' + tr('unavailable') + ')',
            listName: selected.ratingListName || ''
        };
    }

    return { chosen, none, groups, unavailable };
}

// The stored binding for a chosen <select> value. The list name comes from the
// option's data attribute rather than from its text: the label is grouped under the
// database name, and a list whose own name contains the separator used to come back
// mangled when it was parsed back out.
export function parseRatingListValue(raw, listName) {
    if (!raw) return { ratingDbId: '', ratingListId: '', ratingListName: '' };
    const [ratingDbId, ratingListId] = String(raw).split(RATING_LIST_SEPARATOR);
    return {
        ratingDbId: ratingDbId || '',
        ratingListId: ratingListId || '',
        ratingListName: listName || ''
    };
}

// Recompute every player's displayed rating after the rating type (standard /
// rapid / blitz) changed. Returns null when there is nothing to do, so the caller
// can skip the re-render.
//
// `getDisplayRating(player)` is js/ui/players.js's resolver, injected. A player who
// has no rating in the NEW discipline keeps the one they were showing: losing a
// rating outright on a type change looks like data loss to the arbiter.
export function playersWithUpdatedRatings(players, getDisplayRating) {
    if (!players || players.length === 0) return null;
    return players.map(player => {
        const { display } = getDisplayRating(player);
        let newRating = display;
        if (display === '0' && player.rating && player.rating !== '0') {
            newRating = player.rating;
        }
        return { ...player, rating: newRating };
    });
}

// ── Merging two groups ─────────────────────────────────────────────────────

// Everything the merge picker needs, or the reason it cannot open.
//
//   roundsOf(group) — the group's rounds; injected because the ACTIVE group's
//                     rounds live on state.rounds, not on the group object.
//
// Refusal comes up front rather than after a target has been chosen: neither reason
// has anything to do with which group is picked. Rounds already played were paired
// against a different field — colours, byes and tie-breaks all refer to it — so a
// paired group cannot give its players away, and a paired group cannot take any.
export function buildMergePicker(groups, sourceId, roundsOf) {
    const list = Array.isArray(groups) ? groups : [];
    const source = findGroupById(list, sourceId);
    if (!source) return { ok: false, reason: 'no_source', source: null };
    if (!hasMultipleGroups(list)) return { ok: false, reason: 'only_one', source };
    if ((roundsOf(source) || []).length > 0) return { ok: false, reason: 'already_paired', source };

    const candidates = list.filter(g => String(g.id) !== String(source.id));
    const options = candidates.map(g => {
        const paired = (roundsOf(g) || []).length > 0;
        return {
            value: String(g.id),
            label: paired ? `${g.name} (${tr('already paired')})` : g.name,
            disabled: paired
        };
    });
    const firstOpen = candidates.find(g => (roundsOf(g) || []).length === 0);

    return { ok: true, source, options, value: firstOpen ? String(firstOpen.id) : '' };
}

// ── Submitting the form ────────────────────────────────────────────────────

// The tournament-level half of the submitted form. Trimming happens here rather
// than at each input, so a name of spaces fails validation like an empty one.
export function buildGeneralFormData(values = {}) {
    const v = (k) => {
        const raw = values[k];
        return raw === undefined || raw === null ? '' : String(raw);
    };
    return {
        ...tournamentOrganizerFields(values.organizerId, values.organizerName),
        name: v('name').trim(),
        startDate: v('startDate'),
        startTime: v('startTime'),
        endDate: v('endDate'),
        endTime: v('endTime'),
        location: v('location'),
        timeControl: v('timeControl').trim(),
        arbiterName: v('arbiterName').trim(),
        arbiterEmail: v('arbiterEmail').trim(),
        arbiterPhone: v('arbiterPhone').trim(),
        isPublic: !!values.isPublic,
        // '' = ask each time Print Pairings is used (js/ui/print.js)
        pairingPrintStyle: v('pairingPrintStyle'),
        // Anything but the explicit 'manual' means publish on pairing, so a
        // tournament saved before this setting existed keeps behaving as it did.
        pairingPublish: values.pairingPublish === 'manual' ? 'manual' : 'auto',
        welcomeMessage: v('welcomeMessage'),
        // Tournament-level online registration (used for single-group tournaments)
        registrationOpen: !!values.registrationOpen,
        registrationDeadline: v('registrationDeadline'),
        registrationDeadlineTime: v('registrationDeadlineTime'),
        requirePhone: !!values.requirePhone,
        // Checked against NSF's register on the Players page (js/services/nsf-membership.js).
        // Like the registration settings above, the top-level value only counts for a
        // tournament without groups; each group carries its own.
        requireNsfMembership: !!values.requireNsfMembership,

        nsfSeries: nsfSeriesValue(values.nsfSeries),
        registrationApproval: v('registrationApproval'),
        keepRegistrationOpen: !!values.keepRegistrationOpen,
        maxParticipants: normalizeMaxParticipants(v('maxParticipants')),
        waitingList: !!values.waitingList
    };
}

// The settings half, used only when the tournament has no groups of its own.
// roundsCount is the canonical field for Firestore persistence and the real-time
// sync-back; it is kept in step with `rounds` or edits silently revert.
export function buildSingleGroupFormData(values = {}, extras = {}) {
    const rounds = parseInt(values.rounds, 10) || 7;
    return {
        format: values.format,
        type: values.type,
        rounds,
        roundsCount: rounds,
        seedColor: values.seedColor,
        pointSystem: values.pointSystem,
        teamSettings: extras.teamSettings,
        konradSettings: extras.konradSettings,
        acceleration: extras.acceleration,
        ...(extras.ratingListSelection || {})
    };
}

// A multi-group tournament keeps its settings on its groups, so only the general
// half is written; a single-group one takes both halves.
export function combineFormData(general, singleGroup, hasGroups) {
    return hasGroups ? { ...general } : { ...general, ...singleGroup };
}

// The checks the submit handler runs before touching anything. Returns
// { ok: true } or the dialog to show: `kind` picks Dialog.error vs Dialog.warn.
export function validateTournamentForm(formData = {}, userProfile = null) {
    if (!formData.name) {
        return {
            ok: false,
            kind: 'error',
            title: tr('Validation Error'),
            message: tr('Tournament Name is required.')
        };
    }
    if (formData.isPublic && !formData.startDate) {
        return {
            ok: false,
            kind: 'error',
            title: tr('Validation Error'),
            message: tr('Start Date is required to make the tournament public.')
        };
    }
    // A public tournament carries the arbiter's name and country on the public
    // list, so the profile has to hold them first.
    if (formData.isPublic) {
        const p = userProfile;
        if (!p || !p.givenName || !p.lastName || !p.country) {
            return {
                ok: false,
                kind: 'warn',
                title: tr('Profile Incomplete'),
                message: tr('To make a tournament public, you must first complete your profile (Given Name, Last Name, and Country) in the Profile tab.')
            };
        }
    }
    return { ok: true };
}

// An edited tournament: the form's fields written over the stored ones, with the
// groups array preserved (it is not on the form, and spreading formData would drop
// a multi-group event's entire structure).
export function mergeEditedTournament(existing, formData) {
    const existingGroups = existing && existing.groups;
    const next = { ...existing, ...formData };
    if (existingGroups) next.groups = existingGroups;
    return next;
}

// A drafted multi-group tournament promoted to a real one. Returns the tournament
// plus the cursor the app should adopt; `activeGroupId` is null (and players/rounds
// null, meaning "leave them alone") when the draft somehow has no groups.
export function promoteDraftTournament(draft, formData, id) {
    const draftGroups = (draft && draft.groups) || [];
    const tournament = { ...draft, ...formData, id };
    tournament.groups = draftGroups;

    if (draftGroups.length === 0) {
        return { tournament, activeGroupId: null, players: null, rounds: null };
    }

    const first = draftGroups[0];
    tournament.activeGroupId = first.id;
    return {
        tournament,
        activeGroupId: first.id,
        // Deep-cloned: the active group's players and rounds are edited through
        // state.players/state.rounds, and sharing the arrays would edit both copies.
        players: JSON.parse(JSON.stringify(first.players || [])),
        rounds: JSON.parse(JSON.stringify(first.rounds || []))
    };
}

// A brand new single-group tournament.
export function buildNewTournament(formData, { id, draftTiebreaks, invitedEmails } = {}) {
    return {
        id,
        ...formData,
        tiebreaks: tiebreaksOrDefault(draftTiebreaks),
        pendingCooperatingArbiters: [...(invitedEmails || [])]
    };
}

// The registration-link configuration staged during creation, before the tournament
// had an id to attach it to — as the patch to apply to the new tournament, or null
// when nothing was staged.
//
// requirePhone deliberately mirrors the configured field rather than the checkbox:
// the field editor is the more specific statement of the two. (It is left undefined,
// not false, when the config carries no fields at all — the same value the inline
// version produced, and the shape the rest of the app already tolerates.)
export function registrationApiPatch(draftRegistrationApi) {
    if (!draftRegistrationApi) return null;
    return {
        registrationApi: draftRegistrationApi,
        requirePhone: draftRegistrationApi.fields && draftRegistrationApi.fields.phone === 'required'
    };
}

// ── Location autocomplete (Nominatim) ──────────────────────────────────────

// Nominatim's display_name leads with the bare house number for street addresses
// ("30, Hans Burums vei, ..."). Rebuild that into a readable "road number" label;
// leave named places (venues, cities) as their leading part.
export function nominatimPrimaryLabel(item) {
    const addr = item.address || {};
    const firstPart = (item.display_name.split(',')[0] || '').trim();
    if (addr.house_number && firstPart === addr.house_number) {
        const road = addr.road || addr.pedestrian || addr.footway || '';
        return road ? `${road} ${addr.house_number}` : firstPart;
    }
    return firstPart;
}

// What lands in the Location box when a suggestion is picked: the place itself, its
// town and its country, skipping a town that merely repeats the place's own name.
export function nominatimAddressLabel(item) {
    const addr = item.address || {};
    const main = nominatimPrimaryLabel(item);
    const city = addr.city || addr.town || addr.village || addr.suburb || '';
    const country = addr.country || '';

    let cleanAddress = main;
    if (city && city !== main) cleanAddress += `, ${city}`;
    if (country) cleanAddress += `, ${country}`;
    return cleanAddress;
}
